import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useMemo, useRef, } from 'react';
import { cx, } from '../utils/ReactUtils.js';
import { getComponentPositionClass, } from './components.js';
export function Checkbox({ label, value, onChange, checkedValue = true, uncheckedValue = false, indeterminateValue = undefined, mode = 'binary', isEqual = Object.is, error, reserveErrorSpace = 0, disabled = false, className = '', position = 'normal', id, inputRef, ...rest }) {
    const autoId = useId();
    let resolvedId = `checkbox-${autoId}`;
    if (typeof id === 'string' && id.length > 0) {
        resolvedId = id;
    }
    const errorId = `${resolvedId}-error`;
    const state = useMemo(() => {
        let nextState = 'indeterminate';
        if (isEqual(value, checkedValue)) {
            nextState = 'checked';
        }
        else if (isEqual(value, uncheckedValue)) {
            nextState = 'unchecked';
        }
        return nextState;
    }, [value, checkedValue, uncheckedValue, isEqual]);
    let hasError = false;
    let hasErrorMessage = false;
    let errorText = '';
    if (error === true) {
        hasError = true;
    }
    else if (typeof error === 'string' && error.length > 0) {
        hasError = true;
        hasErrorMessage = true;
        errorText = error;
    }
    const localRef = useRef(null);
    const setRefs = useCallback((node) => {
        localRef.current = node;
        if (typeof inputRef === 'function') {
            inputRef(node);
            return;
        }
        if (inputRef) {
            ;
            inputRef.current = node;
        }
    }, [inputRef]);
    useEffect(() => {
        const el = localRef.current;
        if (!el) {
            return;
        }
        el.indeterminate = state === 'indeterminate';
    }, [state]);
    const nextValue = useCallback(() => {
        let next;
        if (mode === 'binary') {
            if (state === 'checked') {
                next = uncheckedValue;
            }
            else {
                next = checkedValue;
            }
            return next;
        }
        if (state === 'indeterminate') {
            next = checkedValue;
        }
        else if (state === 'checked') {
            next = uncheckedValue;
        }
        else {
            next = indeterminateValue;
        }
        return next;
    }, [mode, state, checkedValue, uncheckedValue, indeterminateValue]);
    const handleClick = e => {
        e.preventDefault();
        if (disabled) {
            return;
        }
        onChange(nextValue());
    };
    const boxBase = 'grid place-items-center h-5 w-5 shrink-0 rounded border transition '
        + 'hover:brightness-110 active:brightness-90 '
        + 'peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2';
    let boxColor = '';
    if (hasError) {
        if (state === 'unchecked') {
            boxColor = 'border-error bg-white text-error peer-focus-visible:ring-error';
        }
        else {
            boxColor = 'border-error bg-error text-white peer-focus-visible:ring-error';
        }
    }
    else {
        if (state === 'unchecked') {
            boxColor =
                'border-tym-component-border-default bg-white hover:border-tym-component-border-hover text-gray-900 peer-focus-visible:ring-tym-primary';
        }
        else {
            boxColor = 'border-tym-primary bg-tym-primary text-white peer-focus-visible:ring-tym-primary';
        }
    }
    let labelColor = '';
    if (hasError) {
        labelColor = 'text-error';
    }
    let labelStateClass = 'cursor-pointer';
    if (disabled) {
        labelStateClass = 'cursor-not-allowed opacity-50';
    }
    let icon = null;
    if (state === 'checked') {
        icon = (_jsx("svg", { viewBox: "0 0 20 20", className: "h-4 w-4", fill: "none", children: _jsx("path", { d: "M4.5 10.5l3.2 3.2L15.8 5.6", stroke: "currentColor", strokeWidth: "2.2", strokeLinecap: "round", strokeLinejoin: "round" }) }));
    }
    else if (state === 'indeterminate') {
        icon = (_jsx("svg", { viewBox: "0 0 20 20", className: "h-4 w-4", fill: "none", children: _jsx("path", { d: "M5 10h10", stroke: "currentColor", strokeWidth: "2.2", strokeLinecap: "round" }) }));
    }
    let ariaChecked = false;
    if (state === 'checked') {
        ariaChecked = true;
    }
    else if (state === 'indeterminate') {
        ariaChecked = 'mixed';
    }
    let ariaInvalid;
    if (hasError) {
        ariaInvalid = true;
    }
    else {
        ariaInvalid = undefined;
    }
    let ariaDescribedBy;
    if (hasErrorMessage) {
        ariaDescribedBy = errorId;
    }
    else {
        ariaDescribedBy = undefined;
    }
    let errorMinHeight;
    if (reserveErrorSpace > 0) {
        errorMinHeight = { minHeight: `${reserveErrorSpace * 1.25}rem` };
    }
    else {
        errorMinHeight = undefined;
    }
    const positionClass = getComponentPositionClass(position);
    let labelNode = null;
    if (label) {
        labelNode = _jsx("span", { className: "leading-5", children: label });
    }
    return (_jsxs("div", { className: `w-full ${positionClass} ${className}`, children: [_jsxs("label", { htmlFor: resolvedId, className: cx('inline-flex min-h-10 items-center gap-2 select-none', labelStateClass, labelColor), children: [_jsx("input", { ...rest, ref: setRefs, id: resolvedId, type: "checkbox", disabled: disabled, checked: state === 'checked', readOnly: true, onClick: handleClick, className: "peer sr-only", "aria-invalid": ariaInvalid, "aria-describedby": ariaDescribedBy, "aria-checked": ariaChecked }), _jsx("span", { className: cx(boxBase, boxColor), "aria-hidden": "true", children: icon }), labelNode] }), (errorMinHeight || errorText) && (_jsx("p", { id: errorId, className: "input-error", style: errorMinHeight, children: errorText }))] }));
}
//# sourceMappingURL=Checkbox.js.map